import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import HeroImage from "./HeroImage";
import { HeartbeatAnimation } from "@/components/ui/heartbeat-animation";
import { HeartbeatLine } from "@/components/ui/heartbeat-line";
import { ChevronDown } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { HeartbeatVisualization } from "./HeartbeatVisualization";
import { ToolkitModal } from "./ToolkitModal";

export const HeroSection = () => {
  const [scrolled, setScrolled] = useState(false);
  const [heartbeatHovered, setHeartbeatHovered] = useState(false);
  const [toolkitOpen, setToolkitOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      if (position > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  
  return (
    <>
      <div className="relative flex flex-col-reverse md:flex-row items-center justify-between gap-8 py-0 rounded-2xl px-8 overflow-hidden medical-texture -mt-8 border-t-0
      ">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px] z-0"></div>
        
        {/* Heartbeat line animation - visible only on desktop */}
        {isDesktop && (
          <div 
            className="absolute inset-x-0 top-0 h-full pointer-events-auto z-10 transition-opacity duration-300 -mt-32"
            style={{ opacity: heartbeatHovered ? 0.85 : 0.65 }}
          >
            {/* Suppression des lignes de battement de cœur */}
            {/* Positioning optimized for professional appearance */}
            <div className="absolute inset-x-0 bottom-0 opacity-25 hover:opacity-40 transition-opacity duration-300 z-20">
              <HeartbeatVisualization />
            </div>
          </div>
        )}
        
        {/* More subtle heartbeat animation */}
        <div className="absolute inset-0 z-5 pointer-events-none opacity-50">
          <HeartbeatAnimation count={3} />
        </div>
        
        {/* Main content */}
        <div 
          className="flex-1 space-y-6 relative z-20"
          onMouseEnter={() => setHeartbeatHovered(true)}
          onMouseLeave={() => setHeartbeatHovered(false)}
        >
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Votre santé, notre priorité
            </h1>
            <p className="hidden md:block text-xl text-muted-foreground mt-4">
              Gérez votre santé en toute simplicité avec Medicace, votre compagnon santé personnel
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg" 
              className="medical-button group overflow-hidden relative" 
              onClick={() => setToolkitOpen(true)}
            >
              <span className="relative z-10 group-hover:translate-x-1 transition-transform">
                Commencer
              </span>
              <span className="absolute inset-0 bg-blue-700/80 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
            </Button>
            
            <Button size="lg" variant="outline" className="fill-button group hover:border-blue-300 transition-colors duration-300" asChild>
              <Link to="/about">
                <span>En savoir plus</span>
                <span className="icon ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">❤️</span>
              </Link>
            </Button>
          </div>
        </div>
        
        <div className="flex-1 relative animate-fade-in" style={{
          animationDelay: "0.3s"
        }}>
          <div className="heartbeat-bg relative z-20">
            <HeroImage />
          </div>
        </div>
      </div>
      
      <ToolkitModal isOpen={toolkitOpen} onClose={() => setToolkitOpen(false)} />
    </>
  );
};
